import React, { useState } from "react";
import {
  File, FileArchive, FileImage, FileSpreadsheet, FileText, FilterX, Film, FolderOpen, Upload, Video,
} from "lucide-react";
import {
  ActionMenu, ActionMenuDivider, ActionMenuItem, AppButton, AppDataTable, Callout, EmptyState, FilterToolbar,
  StatusBadge, applyColumnFilters, isColumnFilterActive, sortRows,
} from "../../ui";
import AccessBadge from "../../ContentProtection/AccessBadge";
import { formatBytes, formatDate, DASH } from "../../../utils/format";
import { categoryLabel, plural, videoLength } from "./courseDetails";

const ICONS = {
  PDF: FileText, DOC: FileText, DOCX: FileText, PPT: FileText, PPTX: FileText, TXT: FileText,
  XLS: FileSpreadsheet, XLSX: FileSpreadsheet, CSV: FileSpreadsheet,
  JPG: FileImage, JPEG: FileImage, PNG: FileImage, GIF: FileImage, WEBP: FileImage,
  ZIP: FileArchive, RAR: FileArchive,
};

const WORDS = {
  video: {
    noun: "video",
    nouns: "videos",
    icon: Video,
    upload: "Upload video",
    emptyTitle: "No course videos yet",
    emptyText: "Videos here are shared by every batch of this course. Mark one as a free preview and students who have not enrolled can watch it.",
  },
  document: {
    noun: "file",
    nouns: "files",
    icon: FolderOpen,
    upload: "Upload material",
    emptyTitle: "No course materials yet",
    emptyText: "Files here — notes, slides, scripts — are shared by every batch of this course.",
  },
};

const isReady = (m) => !m.status || m.status === "ready";

const FileCell = ({ material, kind }) => {
  const type = String(material.fileType || "").toUpperCase();
  const Icon = kind === "video" ? Film : ICONS[type] || File;
  const sub = [material.topic, kind === "document" && type].filter(Boolean).join(" · ");
  return (
    <span className="tw-flex tw-min-w-0 tw-items-center tw-gap-3">
      <span className="tw-flex tw-h-9 tw-w-9 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-md tw-bg-surface-sunken tw-text-content-muted">
        <Icon size={18} aria-hidden="true" />
      </span>
      <span className="tw-min-w-0">
        <span className="tw-flex tw-items-center tw-gap-2">
          <span className="tw-truncate tw-font-medium tw-text-content">{material.title || material.fileName}</span>
          {material.status === "processing" && <StatusBadge status="warning">Processing</StatusBadge>}
          {material.status === "failed" && <StatusBadge status="danger">Upload failed</StatusBadge>}
        </span>
        {sub && <span className="tw-block tw-truncate tw-text-xs tw-text-content-subtle">{sub}</span>}
      </span>
    </span>
  );
};

/**
 * CourseFilesTab — the course's own videos or its own materials: files every
 * batch of the course shares, as opposed to a batch's.
 *
 * Opening a file goes through the page's `useMaterialViewer`, so a document is
 * read in the app's viewer and a video plays in its player — never a raw link.
 *
 * Props:
 *   kind       — "video" | "document"
 *   files      — this kind's materials
 *   loading / error / onRetry — the files read, shared by both tabs
 *   truncated  — { shown, total } when the read was capped, else null
 *   onUpload / onOpen / onDownload / onEdit / onDelete — (material) => void
 */
const CourseFilesTab = ({
  kind, files, loading, error, onRetry, truncated, onUpload, onOpen, onDownload, onEdit, onDelete,
}) => {
  const words = WORDS[kind];
  const [columnFilters, setColumnFilters] = useState({});
  const [sort, setSort] = useState({ key: "added", direction: "desc" });

  const columns = [
    {
      key: "title",
      label: kind === "video" ? "Video" : "File",
      sortable: true,
      filter: { type: "text", placeholder: "Title or topic contains…" },
      filterValue: (m) => [m.title, m.topic, m.fileName].filter(Boolean).join(" "),
      sortValue: (m) => m.title || m.fileName || "",
      render: (m) => <FileCell material={m} kind={kind} />,
    },
    kind === "video"
      ? {
        key: "length",
        label: "Length",
        sortable: true,
        sortValue: (m) => Number(m.duration) || null,
        render: (m) => <span className="tw-whitespace-nowrap tw-text-content-muted">{videoLength(m.duration) || DASH}</span>,
      }
      : {
        key: "category",
        label: "Type",
        render: (m) => <span className="tw-text-content-muted">{categoryLabel(m.category)}</span>,
      },
    kind === "video"
      ? {
        key: "preview",
        label: "Who can watch",
        render: (m) => (m.isPreview
          ? <StatusBadge status="success">Free preview</StatusBadge>
          : <span className="tw-text-content-muted">Enrolled students</span>),
      }
      : {
        key: "access",
        label: "Students may",
        render: (m) => (m.access?.level && m.access.level !== "download"
          ? <AccessBadge access={m.access} />
          : <span className="tw-text-content-muted">View and download</span>),
      },
    {
      key: "size",
      label: "Size",
      align: "right",
      sortable: true,
      sortValue: (m) => Number(m.fileSize) || null,
      render: (m) => (
        <span className="tw-whitespace-nowrap tw-tabular-nums tw-text-content-muted">
          {m.fileSize ? formatBytes(m.fileSize) : DASH}
        </span>
      ),
    },
    {
      key: "added",
      label: "Added",
      sortable: true,
      sortValue: (m) => (m.createdAt ? new Date(m.createdAt).getTime() : null),
      render: (m) => (
        <span className="tw-flex tw-flex-col">
          <span className="tw-whitespace-nowrap tw-text-content-muted">{formatDate(m.createdAt)}</span>
          {m.uploadedBy?.name && <span className="tw-truncate tw-text-xs tw-text-content-subtle">by {m.uploadedBy.name}</span>}
        </span>
      ),
    },
    {
      key: "actions",
      label: <span className="tw-sr-only">Actions</span>,
      pinned: "right",
      align: "right",
      // The row opens the file. React bubbles the menu's clicks and keys up the
      // COMPONENT tree, portal or not, so without this wrapper "Delete" would
      // also open the file — and the row's Enter handler would stop the menu
      // opening from the keyboard at all.
      render: (m) => (
        <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} role="presentation">
          <ActionMenu label={`Actions for “${m.title || m.fileName}”`}>
            {isReady(m) && (
              <ActionMenuItem onClick={() => onOpen(m)}>{kind === "video" ? "Play" : "Open"}</ActionMenuItem>
            )}
            {isReady(m) && <ActionMenuItem onClick={() => onDownload(m)}>Download</ActionMenuItem>}
            <ActionMenuItem onClick={() => onEdit(m)}>Edit details</ActionMenuItem>
            <ActionMenuDivider />
            <ActionMenuItem danger onClick={() => onDelete(m)}>Delete</ActionMenuItem>
          </ActionMenu>
        </div>
      ),
    },
  ];

  const filtersOn = Object.values(columnFilters).some(isColumnFilterActive);
  const clearFilters = () => setColumnFilters({});
  const rows = sortRows(applyColumnFilters(files, columns, columnFilters), columns, sort);

  const toolbar = (
    <FilterToolbar
      flush
      actions={(
        <AppButton size="sm" onClick={onUpload}>
          <Upload size={16} aria-hidden="true" /> {words.upload}
        </AppButton>
      )}
    >
      <span className="tw-text-[0.8125rem] tw-text-content-subtle" aria-live="polite">
        {loading ? "Loading…" : plural(rows.length, words.noun, words.nouns)}
      </span>
      {filtersOn && (
        <AppButton size="sm" variant="outline" onClick={clearFilters}>
          <FilterX size={16} aria-hidden="true" /> Clear filters
        </AppButton>
      )}
    </FilterToolbar>
  );

  return (
    <>
      {error && (
        <Callout
          tone="danger"
          role="alert"
          className="tw-mb-3"
          action={<AppButton size="xs" variant="outline" onClick={onRetry}>Try again</AppButton>}
        >
          {error?.response?.data?.message || "Could not load this course's files."}
        </Callout>
      )}
      {truncated && (
        <Callout tone="info" className="tw-mb-3">
          Showing the newest {truncated.shown} of {truncated.total} course files.
        </Callout>
      )}

      <AppDataTable
        fill={false}
        toolbar={toolbar}
        columns={columns}
        rows={rows}
        rowKey={(m) => String(m._id)}
        onRowClick={(m) => { if (isReady(m)) onOpen(m); }}
        sort={sort}
        onSortChange={(key, direction) => setSort({ key, direction })}
        columnFilters={columnFilters}
        onColumnFilterChange={(key, value) => setColumnFilters((prev) => ({ ...prev, [key]: value }))}
        loading={loading}
        emptyState={filtersOn ? (
          <EmptyState
            icon={words.icon}
            title={`No ${words.nouns} match`}
            action={<AppButton size="sm" variant="outline" onClick={clearFilters}>Clear filters</AppButton>}
          />
        ) : (
          <EmptyState
            icon={words.icon}
            title={words.emptyTitle}
            description={words.emptyText}
            action={(
              <AppButton onClick={onUpload}>
                <Upload size={16} aria-hidden="true" /> {words.upload}
              </AppButton>
            )}
          />
        )}
      />
    </>
  );
};

export default CourseFilesTab;
